import type {
  ApplicantOutcomeV2,
  ApplicantSectionV2,
  DashboardSaveV2Request,
  DashboardSaveV2Response,
  DscWorkbenchDashboardResponseV2,
  VehicleSectionV2,
} from "@credit-acceptance/step6-bff-client";
import { useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { useCallback, useState } from "react";

import { useSaveableSectionsStore } from "../../stores/saveableSectionsStore";
import { stipulationKeys } from "../stipulations/api/stipulationKeys";
import { applicationStatusKeys } from "./api/applicationStatusKeys";
import { dscWorkbenchKeys } from "./api/dscWorkbenchKeys";
import { useSaveWorkbench } from "./api/useSaveWorkbench";

/**
 * Result of a saveAll attempt — `apiCallMade` tells callers whether the
 * outcome came back from the save API (vs. a pre-flight short-circuit like no
 * sections being registered), so they can decide whether it's appropriate to
 * surface as an API-response toast.
 */
export interface SaveAllResult {
  ok: boolean;
  apiCallMade: boolean;
  /**
   * HTTP status of the save response, when the API was actually called —
   * any 2xx lands here as `ok: true`, but callers that need to distinguish
   * an exact 200 (e.g. the footer's success toast) can check this directly.
   */
  statusCode?: number;
}

/**
 * Drops an applicant's save outcome (saved, changedFields, warnings, perStep, …), leaving the GET-side section.
 * @param {ApplicantOutcomeV2} [applicant] - The save response's applicant.
 * @returns {ApplicantSectionV2 | undefined} The GET-shaped applicant.
 */
function toApplicantSection(applicant?: ApplicantOutcomeV2): ApplicantSectionV2 | undefined {
  return (
    applicant && {
      present: applicant.present,
      applicantId: applicant.applicantId,
      creditScore: applicant.creditScore,
      residence: applicant.residence,
      income: applicant.income,
      bank: applicant.bank,
      validations: applicant.validations,
    }
  );
}

/**
 * Drops the vehicle's save outcome (saved, changedFields, validations), leaving the GET-side section.
 * @param {VehicleSectionV2} [vehicle] - The save response's vehicle.
 * @returns {VehicleSectionV2 | undefined} The GET-shaped vehicle.
 */
function toVehicleSection(vehicle?: VehicleSectionV2): VehicleSectionV2 | undefined {
  return (
    vehicle && {
      vehicleOnDeal: vehicle.vehicleOnDeal,
      insurance: vehicle.insurance,
      licensePlateNo: vehicle.licensePlateNo,
    }
  );
}

/**
 * Builds the Dashboard query cache snapshot from a v2 save response, which carries the same refreshed sections as
 * the GET. Sections are taken as returned — one absent after a failed read stays absent, with its warning in
 * `dashboardMetadata`. Only `stipulations` and `autopay`, which the save response never carries, come from the cache.
 * @param {DashboardSaveV2Response} response - The v2 save response.
 * @param {DscWorkbenchDashboardResponseV2} [cached] - The cached GET snapshot.
 * @returns {DscWorkbenchDashboardResponseV2} The refreshed dashboard snapshot.
 */
function toDashboardSnapshot(
  response: DashboardSaveV2Response,
  cached?: DscWorkbenchDashboardResponseV2
): DscWorkbenchDashboardResponseV2 {
  return {
    metadata: response.dashboardMetadata,
    approvalId: response.approvalId,
    dealSummary: response.dealSummary,
    driver: toApplicantSection(response.driver),
    secondSigner: toApplicantSection(response.secondSigner),
    vehicle: toVehicleSection(response.vehicle),
    financial: response.financial,
    dealStatus: response.dealStatus,
    dealStatusFlags: response.dealStatusFlags,
    additionalProducts: response.additionalProducts,
    submitChecklist: response.submitChecklist,
    openStipulationCounts: response.openStipulationCounts,
    aggregatorPartner: response.aggregatorPartner,
    stipulations: cached?.stipulations,
    autopay: cached?.autopay,
  };
}

/**
 * Merges section contributions into one save request. Top-level keys are last-wins, except
 * `additionalProducts` (`vsc` / `gap` / `obp` legs), which come from different sections and are merged per key so one
 * section doesn't clobber another's.
 * @param {Partial<DashboardSaveV2Request>[]} payloads - Each registered section's contribution.
 * @returns {DashboardSaveV2Request} The aggregate save request.
 */
function mergePayloads(payloads: Array<Partial<DashboardSaveV2Request>>): DashboardSaveV2Request {
  const merged: DashboardSaveV2Request = Object.assign({}, ...payloads);
  const additionalProducts = Object.assign({}, ...payloads.map((payload) => payload.additionalProducts));
  return {
    ...merged,
    ...(Object.keys(additionalProducts).length > 0 && { additionalProducts }),
  };
}

/**
 * Orchestrates the shared footer Save button and the Exit modal's Save & Exit:
 * runs every registered section's validation (so inline field errors still
 * surface), merges their payload contributions into one v2 Dashboard-shaped
 * save request (`DashboardSaveV2Request`), and issues a single PUT
 * /api/v2/workbench/applications/{approvalId}/Dashboard call regardless of
 * validation outcome — the BFF, not the client, is the source of truth for
 * whether a save succeeds. The only client-side stop is a contributing
 * section's `saveBlocker`, which throws its message before any request.
 * `ok`/`apiCallMade` reflect the actual save API
 * response, and each section's own `onSaved`/`onSaveFailed` decides how it
 * surfaces that result.
 *
 * On any successful (2xx) save, the response already carries a freshly
 * refreshed full Dashboard snapshot, so that's hydrated straight into the
 * Dashboard query cache via `setQueryData` — no follow-up GET — unless the
 * refresh failed entirely (`dashboardMetadata` absent), in which case this
 * falls back to invalidating the query so React Query refetches it. The
 * stipulations list query is still invalidated separately: `stipulationActionsResult`
 * on the response is only a workflow-execution status, not the list itself.
 * The application status is refetched as well, since a save can advance a
 * pre-approval deal.
 * `isSaving` stays true through this whole tail, not just the save request
 * itself, so the footer's Save button can't be re-clicked mid-chain.
 * @param {(payload: DashboardSaveV2Request) => void} [assertCanSave] - Rechecks each contribution's live access after validation, before writing.
 * @returns {{saveAll: (approvalId: string) => Promise<SaveAllResult>, isSaving: boolean}} The orchestrator.
 */
export function useSaveAllSections(assertCanSave?: (payload: DashboardSaveV2Request) => void) {
  const saveWorkbench = useSaveWorkbench();
  const queryClient = useQueryClient();
  const [isSequencingFollowUps, setIsSequencingFollowUps] = useState(false);

  const saveAll = useCallback(
    async (approvalId: string): Promise<SaveAllResult> => {
      const entries = [...useSaveableSectionsStore.getState().sections.entries()];
      // Fail closed if nothing has registered yet (e.g. Save clicked before a
      // section mounts) rather than silently succeeding an empty-payload save.
      if (entries.length === 0) {
        return { ok: false, apiCallMade: false };
      }

      // Run validation for its side effect (surfacing inline errors); the
      // result no longer gates the save — a section stays invalid client-side
      // but still gets saved as-is, so a cleared/blank field always persists.
      await Promise.all(entries.map(([, section]) => section.validate()));

      const contributions = entries
        .map(([, section]) => ({ section, payload: section.getPayload() }))
        .filter(({ payload }) => Object.values(payload).some((value) => value !== undefined));
      if (contributions.length === 0) {
        throw new Error(t("workbench.saveNotAllowed"));
      }
      const blocker = contributions.map(({ section }) => section.saveBlocker?.()).find(Boolean);
      if (blocker) {
        throw new Error(blocker);
      }
      const payload = mergePayloads(contributions.map((contribution) => contribution.payload));
      assertCanSave?.(payload);

      try {
        const { response, status } = await saveWorkbench.mutateAsync({ approvalId, payload });
        for (const { section } of contributions) {
          section.onSaved({
            metadata: response.metadata,
            driver: response.driver,
            secondSigner: response.secondSigner,
            additionalProducts: response.additionalProducts,
            vehicle: response.vehicle,
            violations: response.violations,
            ignoredSections: response.ignoredSections,
            refreshFailed: !response.dashboardMetadata,
          });
        }
        // Sequenced follow-up: the stipulations list and application status refreshes, awaited so the
        // Exit modal's move-account call fires only after it has settled.
        setIsSequencingFollowUps(true);
        try {
          if (response.dashboardMetadata) {
            const dashboardKey = dscWorkbenchKeys.detail({ approvalId });
            const cached = queryClient.getQueryData<DscWorkbenchDashboardResponseV2>(dashboardKey);
            queryClient.setQueryData(dashboardKey, toDashboardSnapshot(response, cached));
          } else {
            // The post-save dashboard refresh failed entirely — fall back to
            // invalidating so React Query issues a fresh GET.
            await queryClient.invalidateQueries({ queryKey: dscWorkbenchKeys.detail({ approvalId }) });
          }

          await Promise.all([
            queryClient.invalidateQueries({ queryKey: stipulationKeys.lists() }),
            queryClient.invalidateQueries({ queryKey: applicationStatusKeys.detail({ approvalId }) }),
          ]);
        } finally {
          setIsSequencingFollowUps(false);
        }
        return { ok: true, apiCallMade: true, statusCode: status };
      } catch (error) {
        for (const { section } of contributions) {
          section.onSaveFailed(error);
        }
        return { ok: false, apiCallMade: true };
      }
    },
    [assertCanSave, queryClient, saveWorkbench]
  );

  return { saveAll, isSaving: saveWorkbench.isPending || isSequencingFollowUps };
}
